const services = [
  {
    icon: 'ri-code-box-line',
    title: 'Custom Software Development',
    desc: 'Enterprise-grade web, mobile and API platforms engineered end-to-end. From scoping and UX to CI/CD, we ship products that scale.',
    tags: ['Web', 'Mobile', 'APIs', 'DevOps'],
    accent: 'primary',
  },
  {
    icon: 'ri-server-line',
    title: 'Data Centre — GDB Cloud',
    desc: 'Owned & operated Tier-III grade data centre with private cloud, colocation, DR and hybrid connectivity. Compliance-ready workloads.',
    tags: ['Colocation', 'Private Cloud', 'DR', 'Hybrid'],
    accent: 'accent',
    href: 'https://gdbcloud.com/',
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud Services',
    desc: 'AWS, Azure, GCP and GDB Cloud migrations. Kubernetes, IaC, cost optimisation, FinOps and managed cloud operations for 24×7 reliability.',
    tags: ['Migration', 'K8s', 'FinOps', 'Managed'],
    accent: 'secondary',
  },
  {
    icon: 'ri-robot-2-line',
    title: 'AI Agentic Tools',
    desc: 'Build multi-agent workflows, RAG pipelines, autonomous copilots and vertical AI assistants tailored for enterprise data & governance.',
    tags: ['Agents', 'RAG', 'LLMOps', 'Copilots'],
    accent: 'primary',
  },
  {
    icon: 'ri-coin-line',
    title: 'Real World Asset Tokenization',
    desc: 'End-to-end real estate tokenization — issuance, smart contracts, KYC/AML, cap table, secondary marketplace and regulatory compliance.',
    tags: ['Issuance', 'Compliance', 'Marketplace'],
    accent: 'accent',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Managed Services & Support',
    desc: '24×7 NOC, security monitoring, patching and dedicated engineering pods that operate as an extension of your in-house team.',
    tags: ['NOC', 'SecOps', 'Pods'],
    accent: 'secondary',
  },
];

const accentClasses: Record<string, { bg: string; text: string; ring: string }> = {
  primary: { bg: 'bg-primary-500', text: 'text-primary-700', ring: 'border-primary-200' },
  accent: { bg: 'bg-accent-500', text: 'text-accent-700', ring: 'border-accent-200' },
  secondary: { bg: 'bg-secondary-500', text: 'text-secondary-700', ring: 'border-secondary-200' },
};

export default function Services() {
  return (
    <section id="services" className="relative bg-background-100 py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider">
            <i className="ri-stack-line"></i>
            What we do
          </div>
          <h4 className="mt-5 font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
            <a href="#services" className="hover:text-primary-700 transition-colors cursor-pointer">
              A full-stack technology partner, from silicon to smart contracts.
            </a>
          </h4>
          <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed">
            One team, six deeply integrated practices. We architect, build and operate the systems
            that power modern enterprises — with the reliability of a data centre operator and
            the velocity of a product studio.
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((s) => {
            const ac = accentClasses[s.accent];
            const CardWrap = s.href ? 'a' : 'div';
            return (
              <CardWrap
                key={s.title}
                {...(s.href ? { href: s.href, target: '_blank', rel: 'noopener nofollow' } : {})}
                className={`group relative flex flex-col p-6 md:p-7 rounded-lg bg-background-50 border border-background-200 hover:border-accent-300 transition-all ${s.href ? 'cursor-pointer' : ''}`}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-md ${ac.bg} text-background-50 mb-5`}>
                  <i className={`${s.icon} text-2xl`}></i>
                </div>
                <h4 className="font-heading font-semibold text-xl text-foreground-900 group-hover:text-primary-700 transition-colors">
                  {s.title}
                </h4>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed flex-1">{s.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span key={t} className={`px-2.5 py-1 text-xs font-medium rounded-full bg-background-100 border ${ac.ring} ${ac.text}`}>
                      {t}
                    </span>
                  ))}
                </div>
                {s.href && (
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-accent-600">
                    Visit GDB Cloud
                    <i className="ri-external-link-line"></i>
                  </div>
                )}
              </CardWrap>
            );
          })}
        </div>
      </div>
    </section>
  );
}